'use client';
import React, { useState, useEffect, useCallback } from 'react';
import StepIndicator from './StepIndicator';
import StepLocations from './StepLocations';
import StepPackage from './StepPackage';
import StepPricing from './StepPricing';
import StepPayment from './StepPayment';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtimeOrders, RealtimeOrder, OrderStatus } from '@/hooks/useRealtimeOrders';
import { toast } from 'sonner';
import Link from 'next/link';

export type DeliveryFormData = {
  pickupAddress: string;
  pickupLandmark: string;
  pickupPhone: string;
  deliveryAddress: string;
  deliveryLandmark: string;
  deliveryPhone: string;
  recipientName: string;
  packageType: string;
  packageDescription: string;
  weight: string;
  fragile: boolean;
  distanceKm: number;
  estimatedPrice: number;
  basefare: number;
  distanceCharge: number;
  paymentMethod: string;
  paymentStatus: string;
  notes: string;
};

const INITIAL_FORM: DeliveryFormData = {
  pickupAddress: '',
  pickupLandmark: '',
  pickupPhone: '',
  deliveryAddress: '',
  deliveryLandmark: '',
  deliveryPhone: '',
  recipientName: '',
  packageType: '',
  packageDescription: '',
  weight: '',
  fragile: false,
  distanceKm: 0,
  estimatedPrice: 0,
  basefare: 3500,
  distanceCharge: 0,
  paymentMethod: '',
  paymentStatus: 'pending_payment',
  notes: '',
};

const STEPS = ['Maeneo', 'Kifurushi', 'Bei', 'Malipo'];

const PAYMENT_METHOD_MAP: Record<string, string> = {
  'M-Pesa': 'mpesa',
  'Airtel Money': 'airtel_money',
  'Tigo Pesa': 'tigo_pesa',
  'Cash': 'cash',
};

const PACKAGE_TYPE_MAP: Record<string, string> = {
  'Document': 'document',
  'Parcel': 'parcel',
  'Electronics': 'electronics',
  'Food': 'food',
  'Fragile': 'fragile',
  'Oversized': 'oversized',
};

const STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Inasubiri',
  searching_for_rider: 'Inatafuta Rider',
  rider_assigned: 'Rider Amepewa',
  on_delivery: 'Inafanywa Delivery',
  delivered: 'Imewasilishwa',
  cancelled: 'Imefutwa',
};

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'text-amber-600 bg-amber-50',
  searching_for_rider: 'text-blue-600 bg-blue-50',
  rider_assigned: 'text-indigo-600 bg-indigo-50',
  on_delivery: 'text-orange-600 bg-orange-50',
  delivered: 'text-green-600 bg-green-50',
  cancelled: 'text-red-600 bg-red-50',
};

export default function BookDeliveryWizard() {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<DeliveryFormData>(INITIAL_FORM);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>('pending');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateForm = (partial: Partial<DeliveryFormData>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };

  const nextStep = () => setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const handleOrderConfirm = async () => {
    if (!user?.id) {
      toast.error('Tafadhali ingia kwanza ili kuweka order.');
      return;
    }

    setIsSubmitting(true);
    try {
      const supabase = createClient();
      const paymentMethodKey = PAYMENT_METHOD_MAP[formData.paymentMethod] || 'cash';
      const packageTypeKey = PACKAGE_TYPE_MAP[formData.packageType] || 'parcel';

      const { data, error } = await supabase
        .from('orders')
        .insert({
          customer_id: user.id,
          pickup_address: formData.pickupAddress,
          pickup_landmark: formData.pickupLandmark || null,
          pickup_phone: formData.pickupPhone || null,
          delivery_address: formData.deliveryAddress,
          delivery_landmark: formData.deliveryLandmark || null,
          delivery_phone: formData.deliveryPhone || null,
          recipient_name: formData.recipientName || null,
          package_type: packageTypeKey,
          package_description: formData.packageDescription || null,
          weight_kg: formData.weight ? parseFloat(formData.weight) : null,
          is_fragile: formData.fragile,
          distance_km: formData.distanceKm,
          base_fare: formData.basefare,
          distance_charge: formData.distanceCharge,
          total_amount: formData.estimatedPrice,
          payment_method: paymentMethodKey,
          payment_status: 'pending_payment',
          notes: formData.notes || null,
          status: 'pending',
        })
        .select('id, order_number')
        .single();

      if (error) {
        console.log('Order insert error:', error.message);
        // Fallback: generate local order number for demo
        const num = `PK-${String(Math.floor(4800 + Math.random() * 200)).padStart(6, '0')}`;
        setOrderNumber(num);
        setOrderPlaced(true);
        return;
      }

      setOrderId(data.id);
      setOrderNumber(data.order_number);
      setOrderStatus('pending');
      setOrderPlaced(true);
    } catch (err) {
      console.log('Order confirm error:', err);
      const num = `PK-${String(Math.floor(4800 + Math.random() * 200)).padStart(6, '0')}`;
      setOrderNumber(num);
      setOrderPlaced(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Realtime: track order status updates for this customer's order
  const handleOrderChange = useCallback(
    (payload: {
      eventType: 'INSERT' | 'UPDATE' | 'DELETE';
      newRecord?: RealtimeOrder;
    }) => {
      if (payload.eventType === 'UPDATE' && payload.newRecord && orderId) {
        const updated = payload.newRecord;
        if (updated.id === orderId) {
          const newStatus = updated.status as OrderStatus;
          setOrderStatus(newStatus);
          const label = STATUS_LABELS[newStatus] || newStatus;
          if (newStatus === 'rider_assigned') {
            toast.success(`Rider amepewa order yako! ${orderNumber}`, { duration: 5000 });
          } else if (newStatus === 'on_delivery') {
            toast.success(`Order yako ${orderNumber} inafanywa delivery sasa!`, { duration: 5000 });
          } else if (newStatus === 'delivered') {
            toast.success(`Order ${orderNumber} imewasilishwa! 🎉`, { duration: 6000 });
          } else if (newStatus === 'cancelled') {
            toast.error(`Order ${orderNumber} imefutwa.`, { duration: 5000 });
          } else {
            toast.info(`Hali ya order: ${label}`, { duration: 3000 });
          }
        }
      }
    },
    [orderId, orderNumber]
  );

  useRealtimeOrders({
    customerId: user?.id,
    onOrderChange: handleOrderChange,
  });

  if (orderPlaced) {
    return (
      <OrderConfirmation
        orderNumber={orderNumber}
        formData={formData}
        orderStatus={orderStatus}
        onReset={() => {
          setOrderPlaced(false);
          setFormData(INITIAL_FORM);
          setCurrentStep(0);
          setOrderId(null);
          setOrderStatus('pending');
        }}
        orderId={orderId}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Main Form */}
      <div className="xl:col-span-2">
        <StepIndicator steps={STEPS} currentStep={currentStep} />
        <div className="mt-5 animate-slide-up">
          {currentStep === 0 && (
            <StepLocations formData={formData} updateForm={updateForm} onNext={nextStep} />
          )}
          {currentStep === 1 && (
            <StepPackage formData={formData} updateForm={updateForm} onNext={nextStep} onBack={prevStep} />
          )}
          {currentStep === 2 && (
            <StepPricing formData={formData} updateForm={updateForm} onNext={nextStep} onBack={prevStep} />
          )}
          {currentStep === 3 && (
            <StepPayment
              formData={formData}
              updateForm={updateForm}
              onBack={prevStep}
              onConfirm={handleOrderConfirm}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>

      {/* Sidebar Summary */}
      <div className="xl:col-span-1">
        <OrderSummaryPanel formData={formData} currentStep={currentStep} />
      </div>
    </div>
  );
}

function OrderSummaryPanel({ formData, currentStep }: { formData: DeliveryFormData; currentStep: number }) {
  return (
    <div className="space-y-4">
      {/* Map Preview */}
      <div className="card-elevated overflow-hidden">
        <div className="bg-gradient-to-br from-green-100 to-blue-100 h-48 flex items-center justify-center relative">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 20px, rgba(0,0,0,0.05) 20px, rgba(0,0,0,0.05) 21px), repeating-linear-gradient(90deg, transparent, transparent 20px, rgba(0,0,0,0.05) 20px, rgba(0,0,0,0.05) 21px)',
            }}
          />
          <div className="text-center">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
              <MapPinIcon />
            </div>
            <p className="text-xs font-medium text-muted-foreground">
              {formData.pickupAddress && formData.deliveryAddress ? 'Route calculated' : 'Enter locations to see route'}
            </p>
          </div>
          {formData.pickupAddress && (
            <div className="absolute top-6 left-8 w-5 h-5 bg-green-500 rounded-full border-2 border-white shadow-md flex items-center justify-center">
              <span className="text-[8px] text-white font-bold">A</span>
            </div>
          )}
          {formData.deliveryAddress && (
            <div className="absolute bottom-8 right-10 w-5 h-5 bg-red-500 rounded-full border-2 border-white shadow-md flex items-center justify-center">
              <span className="text-[8px] text-white font-bold">B</span>
            </div>
          )}
        </div>
        {formData.distanceKm > 0 && (
          <div className="px-4 py-3 bg-muted/50 border-t border-border flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-medium">Umbali wa Safari</span>
            <span className="text-sm font-bold text-foreground font-tabular">{formData.distanceKm.toFixed(1)} km</span>
          </div>
        )}
      </div>

      {/* Order Summary Card */}
      <div className="card-elevated p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Muhtasari wa Order</h3>
        <div className="space-y-2.5">
          <SummaryRow label="Pickup" value={formData.pickupAddress || '—'} />
          <SummaryRow label="Delivery" value={formData.deliveryAddress || '—'} />
          <SummaryRow label="Mpokeaji" value={formData.recipientName || '—'} />
          <SummaryRow label="Aina ya Kifurushi" value={formData.packageType || '—'} />
          {formData.weight && <SummaryRow label="Uzito" value={`${formData.weight} kg`} />}
          {currentStep >= 2 && formData.estimatedPrice > 0 && (
            <>
              <div className="border-t border-border pt-2 mt-2">
                <SummaryRow label="Nauli ya Msingi" value={`TSh ${formData.basefare.toLocaleString()}`} />
                <SummaryRow label="Malipo ya Umbali" value={`TSh ${formData.distanceCharge.toLocaleString()}`} />
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                  <span className="text-sm font-bold text-foreground">Jumla</span>
                  <span className="text-lg font-extrabold text-primary tsh-value">TSh {formData.estimatedPrice.toLocaleString()}</span>
                </div>
              </div>
            </>
          )}
          {formData.paymentMethod && <SummaryRow label="Malipo" value={formData.paymentMethod} />}
        </div>
      </div>

      {/* Help */}
      <div className="card-elevated p-4 bg-primary/5 border-primary/20">
        <p className="text-xs font-semibold text-primary mb-1">Unahitaji msaada?</p>
        <p className="text-xs text-muted-foreground">
          Piga simu: <span className="font-semibold text-foreground">+255 754 000 111</span>
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          WhatsApp: <span className="font-semibold text-foreground">+255 754 000 222</span>
        </p>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-xs font-medium text-foreground text-right truncate max-w-[160px]">{value}</span>
    </div>
  );
}

function MapPinIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

const STATUS_STEPS: OrderStatus[] = ['pending', 'searching_for_rider', 'rider_assigned', 'on_delivery', 'delivered'];

function OrderConfirmation({
  orderNumber,
  formData,
  orderStatus,
  onReset,
  orderId,
}: {
  orderNumber: string;
  formData: DeliveryFormData;
  orderStatus: OrderStatus;
  onReset: () => void;
  orderId?: string | null;
}) {
  const currentStepIdx = STATUS_STEPS.indexOf(orderStatus);
  const isDelivered = orderStatus === 'delivered';
  const isCancelled = orderStatus === 'cancelled';

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] animate-slide-up">
      <div className="card-elevated p-8 max-w-md w-full text-center">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${isCancelled ? 'bg-red-100' : 'bg-green-100'}`}>
          {isCancelled ? (
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>

        <h2 className="text-2xl font-extrabold text-foreground mb-1">
          {isCancelled ? 'Order Imefutwa' : 'Order Imethibitishwa!'}
        </h2>
        <p className="text-muted-foreground text-sm mb-5">
          {isCancelled
            ? 'Order yako imefutwa. Unaweza kuweka order mpya.' :'Delivery yako imepokelewa na tutakutumia ujumbe hivi karibuni.'}
        </p>

        <div className="bg-muted rounded-xl p-4 mb-5">
          <p className="text-xs text-muted-foreground mb-1">Nambari ya Order</p>
          <p className="text-3xl font-extrabold text-primary font-tabular tracking-wider">{orderNumber}</p>
        </div>

        {/* Realtime Status Tracker */}
        {!isCancelled && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-foreground">Hali ya Order</p>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                <span className="text-[10px] text-green-600 font-medium">Live</span>
              </div>
            </div>
            <div className="space-y-1.5">
              {STATUS_STEPS.map((step, idx) => {
                const isCompleted = idx < currentStepIdx;
                const isCurrent = idx === currentStepIdx;
                const label = STATUS_LABELS[step];
                return (
                  <div key={step} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all ${
                    isCurrent ? 'bg-primary/10 border border-primary/20' : isCompleted ? 'opacity-60' : 'opacity-30'
                  }`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                      isCompleted ? 'bg-green-500' : isCurrent ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}>
                      {isCompleted ? (
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : isCurrent ? (
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      ) : null}
                    </div>
                    <span className={`font-medium ${isCurrent ? 'text-primary' : isCompleted ? 'text-green-700' : 'text-muted-foreground'}`}>
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-left space-y-2 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Kutoka</span>
            <span className="font-medium text-foreground">{formData.pickupAddress}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Kwenda</span>
            <span className="font-medium text-foreground">{formData.deliveryAddress}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Malipo</span>
            <span className="font-extrabold text-primary tsh-value">TSh {formData.estimatedPrice.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Njia ya Malipo</span>
            <span className="font-medium text-foreground">{formData.paymentMethod}</span>
          </div>
        </div>

        <div className="flex gap-3">
          {!isDelivered && !isCancelled && orderId && (
            <Link
              href={`/track/${orderId}`}
              className="flex-1 btn-primary py-3 rounded-xl text-sm font-semibold text-center"
            >
              Fuatilia Order
            </Link>
          )}
          {!isDelivered && !isCancelled && !orderId && (
            <button className="flex-1 btn-primary py-3 rounded-xl text-sm font-semibold">
              Fuatilia Order
            </button>
          )}
          <button
            onClick={onReset}
            className="flex-1 btn-secondary py-3 rounded-xl text-sm font-semibold"
          >
            {isDelivered ? 'Order Nyingine' : isCancelled ? 'Order Mpya' : 'Order Nyingine'}
          </button>
        </div>
      </div>
    </div>
  );
}